import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getCurrentUser } from "../services/pocketbase";

const ClipUploader = ({ open, onClose }) => {
  const [error, setError] = useState(null);
  const user = getCurrentUser();
  const [formData, setFormData] = useState({
    game: "",
    map: "",
    highest_round: "",
    paypal_handle: "",
    clip_title: "",
    discord_username: user?.username || "",
    consent_promotional: false,
    clip_link: "",
  });

  const games = [
    { value: "blackops6", label: "Black Ops 6" },
    { value: "gta5", label: "GTA 5" },
    { value: "other", label: "Other" },
  ];

  const bo6Maps = [
    { value: "liberty_falls", label: "Liberty Falls" },
    { value: "terminus", label: "Terminus" },
    { value: "cittadelle_de_morts", label: "Cittadelle de Morts" },
    { value: "the_toumb", label: "The Toumb" },
  ];

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "consent_promotional" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.clip_link) {
      setError("Please provide a clip link");
      return;
    }

    try {
      // Create Discord webhook message
      const webhookData = {
        content: "New Clip Submission! <@767408405643788318>",
        embeds: [
          {
            title: "New Gaming Clip",
            fields: [
              {
                name: "Game",
                value: formData.game === "blackops6" ? "Black Ops 6" : "GTA 5",
                inline: true,
              },
              {
                name: "Clip Title",
                value: formData.clip_title,
                inline: true,
              },
              {
                name: "Discord Username",
                value: formData.discord_username,
                inline: true,
              },
            ],
            description: `Clip Link: ${formData.clip_link}\n${
              formData.game === "blackops6"
                ? `Map: ${formData.map}\nHighest Round: ${formData.highest_round}\nPayPal Handle: ${formData.paypal_handle}`
                : ""
            }`,
            color: 0x00ff00,
          },
        ],
      };

      // Send to Discord webhook
      const response = await fetch("/api/webhooks/clip-upload", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });

      if (!response.ok) {
        throw new Error("Failed to send clip to Discord");
      }

      // Reset form and close dialog
      setFormData({
        game: "",
        map: "",
        highest_round: "",
        paypal_handle: "",
        clip_title: "",
        discord_username: user?.username || "",
        consent_promotional: false,
        clip_link: "", // Changed from twitch_link
      });
      onClose(true);

      return await response.json();
    } catch (error) {
      console.error("Error sending webhook:", error);
      throw error;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="sm"
      sx={{ zIndex: 99 }}
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        Submit Gaming Clip
        <IconButton
          aria-label="close"
          onClick={() => onClose(false)}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Game</InputLabel>
              <Select
                name="game"
                value={formData.game}
                onChange={handleInputChange}
                required
              >
                {games.map((game) => (
                  <MenuItem key={game.value} value={game.value}>
                    {game.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.game === "blackops6" && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Map</InputLabel>
                  <Select
                    name="map"
                    value={formData.map}
                    onChange={handleInputChange}
                    required
                  >
                    {bo6Maps.map((map) => (
                      <MenuItem key={map.value} value={map.value}>
                        {map.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Highest Round"
                  name="highest_round"
                  type="number"
                  value={formData.highest_round}
                  onChange={handleInputChange}
                  required
                />
              </>
            )}

            <TextField
              fullWidth
              label="Discord Username"
              name="discord_username"
              value={formData.discord_username}
              disabled
              sx={{ backgroundColor: "rgba(0, 0, 0, 0.05)" }}
            />

            {formData.game === "blackops6" && (
              <>
                <TextField
                  fullWidth
                  label="PayPal Handle"
                  name="paypal_handle"
                  value={formData.paypal_handle}
                  onChange={handleInputChange}
                  helperText="Only required if submitting for a tournament"
                />
              </>
            )}

            <TextField
              fullWidth
              label="Clip Title"
              name="clip_title"
              value={formData.clip_title}
              onChange={handleInputChange}
              required
            />

            <TextField
              fullWidth
              label="Clip Link"
              name="clip_link"
              value={formData.clip_link}
              onChange={handleInputChange}
              required
              helperText="Please paste your clip link here"
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="consent_promotional"
                  checked={formData.consent_promotional}
                  onChange={handleInputChange}
                  required
                />
              }
              label="I consent to my gameplay being used in TOG's promotional media"
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button type="submit" variant="contained">
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ClipUploader;
